import { useMemo, useState } from 'react'
import {
  Button,
  Col,
  Container,
  Nav,
  Row,
  useToaster
} from 'react-angle-dashboard-components'
import { FieldError, useFieldArray, useFormContext } from 'react-hook-form'
import { IQuoteFormData } from '../../QuoteForm'
import { Heading, InvalidFeedback, SmallGrayHeading } from '../../style'
import PlanCard from '../../components/PlanCard/PlanCard'
import CustomPlanFormModal, { ICustomPlanForm } from './CustomPlanFormModal'
import FooterNav from '../../FooterNav'
import {
  useAssociateMedicalPlans,
  useCreateMedPlan,
  useDeleteMedPlan,
  useEditMedPlan,
  useMedicalPlans
} from '../../../../hooks/quote.hooks'
import {
  mapCustomPlanFormToDTO,
  mapMedicalPlanDTOToCustomForm
} from '../../../../services/quote/mappers'
import { IMedicalPlanDTO } from '../../../../services/quote/dto'

const PlanInfoStep1 = () => {
  const { showError } = useToaster()

  const [formModalOpen, setFormModalOpen] = useState(false)
  const [planToEdit, setPlanToEdit] = useState<IMedicalPlanDTO | undefined>()

  const { control, formState, trigger, watch } =
    useFormContext<IQuoteFormData>()

  const quoteId = watch('entryId')

  const errors = formState.errors.planInfo?.step1

  const { data: medicalPlans } = useMedicalPlans(quoteId)

  const { mutateAsync: createPlan } = useCreateMedPlan()

  const { mutateAsync: editPlan } = useEditMedPlan()

  const { mutate: deletePlan } = useDeleteMedPlan()

  const { mutateAsync: associateMedicalPlans } = useAssociateMedicalPlans()

  const anglePlans = useMemo(
    () => medicalPlans?.filter((plan) => plan.planCreator === 'angle'),
    [medicalPlans]
  )

  const brokerPlans = useMemo(
    () => medicalPlans?.filter((plan) => plan.planCreator === 'broker'),
    [medicalPlans]
  )

  const { append, remove, fields } = useFieldArray({
    name: 'planInfo.step1.selectedPlans',
    control
  })

  const [activeTabKey, setActiveTabKey] = useState<'base' | 'custom'>('base')

  const openCustomPlanForm = () => {
    setFormModalOpen(true)
  }

  const onCloseModal = () => {
    setPlanToEdit(undefined)
    setFormModalOpen(false)
  }

  const customPlanSubmit = async (formValues: ICustomPlanForm) => {
    if (planToEdit) {
      // edit
      try {
        await editPlan({
          entryId: planToEdit.entryId,
          data: mapCustomPlanFormToDTO(formValues)
        })
        onCloseModal()
      } catch (err) {
        console.error(err)
        showError('There was an error editing your custom plan')
      }
    } else {
      try {
        await createPlan(mapCustomPlanFormToDTO(formValues))
        onCloseModal()
      } catch (err) {
        console.error(err)
        showError('There was an error creating your custom plan')
      }
    }
  }

  const onContinue = async (callback: () => void) => {
    if (quoteId) {
      try {
        await associateMedicalPlans({
          quoteId: quoteId.toString(),
          data: {
            entryIds: watch('planInfo.step1').selectedPlans.map(({ id }) => id)
          }
        })
        callback()
      } catch (err) {
        showError(`Error associating med plans: ${err}`)
      }
    } else {
      throw new Error('quoteId missing')
    }
  }

  const PlanCards = () => {
    if (activeTabKey === 'base') {
      return (
        <>
          {anglePlans?.map((plan) => {
            const currentSelectedIndex = fields.findIndex(
              (v) => v.id === plan.entryId
            )

            return (
              <Col key={plan.entryId} className="col-auto">
                <PlanCard
                  plan={plan}
                  checked={currentSelectedIndex > -1}
                  onClick={(isSelected) => {
                    isSelected
                      ? append({ id: plan.entryId })
                      : remove(currentSelectedIndex)
                    trigger('planInfo.step1.selectedPlans')
                  }}
                />
              </Col>
            )
          })}
        </>
      )
    }
    if (activeTabKey === 'custom') {
      return (
        <>
          {brokerPlans?.map((plan) => {
            const currentSelectedIndex = fields.findIndex(
              (v) => v.id === plan.entryId
            )

            const onEditPlan = () => {
              setPlanToEdit(plan)
              setFormModalOpen(true)
            }

            const onDeletePlan = () => {
              deletePlan(plan.entryId)
            }

            return (
              <Col key={plan.entryId} className="col-auto">
                <PlanCard
                  plan={plan}
                  checked={currentSelectedIndex > -1}
                  onClick={(isSelected) => {
                    isSelected
                      ? append({ id: plan.entryId })
                      : remove(currentSelectedIndex)
                    trigger('planInfo.step1.selectedPlans')
                  }}
                  onEdit={onEditPlan}
                  onDelete={onDeletePlan}
                />
              </Col>
            )
          })}
        </>
      )
    }
    return null
  }

  return (
    <Container className="p-4">
      <CustomPlanFormModal
        key={JSON.stringify(formModalOpen)}
        open={formModalOpen}
        onClose={onCloseModal}
        formValues={
          planToEdit ? mapMedicalPlanDTOToCustomForm(planToEdit) : planToEdit
        }
        onSubmit={customPlanSubmit}
      />
      <Row className="d-flex justify-content-between align-items-center pb-5">
        <Col>
          <SmallGrayHeading>Plan Info (1/2)</SmallGrayHeading>
          <Heading className="pt-1">Select Your Plans</Heading>
          {errors?.selectedPlans && (
            <InvalidFeedback>
              {(errors?.selectedPlans as unknown as FieldError)?.message}
            </InvalidFeedback>
          )}
        </Col>
        <Button
          variant="outline-primary d-flex align-items-center"
          onClick={openCustomPlanForm}
        >
          <i
            className="icon icon-plus fs-12 mr-2"
            style={{ height: '100%', width: '0.8rem' }}
          />
          <span>Add a custom plan</span>
        </Button>
      </Row>
      <Nav
        variant="tabs"
        activeKey={activeTabKey}
        onSelect={(key) => {
          if (key === 'base' || key === 'custom') {
            setActiveTabKey(key)
          }
        }}
        className="mt-5"
      >
        <Nav.Item className="mr-3">
          <Nav.Link eventKey="base">Angle Base Plans</Nav.Link>
        </Nav.Item>
        <Nav.Item className="mr-3">
          <Nav.Link eventKey="custom">Custom Plans</Nav.Link>
        </Nav.Item>
      </Nav>
      <div className="py-4">
        <Row className="g-2 d-flex justify-content-center">
          <PlanCards />
        </Row>
      </div>
      <FooterNav triggerPath="planInfo.step1" onContinue={onContinue} />
    </Container>
  )
}

export default PlanInfoStep1
